import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AnimatedSection, TextReveal, MagneticButton } from "./animations";
import { ArrowUpRight, Mail } from "lucide-react";
import ContactForm from "./ContactForm";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[hsl(var(--block-dark))] text-white rounded-t-xl overflow-hidden">
      {/* CONTACT Header */}
      <div className="px-6 md:px-12 py-16 md:py-24 flex flex-col md:flex-row items-start md:items-end justify-between gap-8 overflow-visible">
        <div className="flex items-baseline gap-2">
          <TextReveal delay={0.2}>
            <h2 className="font-display text-[clamp(4rem,18vw,14rem)] leading-[0.85] uppercase tracking-tighter overflow-visible">
              CONTACT
            </h2>
          </TextReveal>
          <motion.span
            className="font-mono text-sm md:text-base text-accent font-medium"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            .04
          </motion.span>
        </div>

        <AnimatedSection delay={0.4} direction="left">
          <span className="leading-relaxed tracking-tight text-white/40 text-lg md:text-xl font-sans max-w-md block">
            Open to conversations with organizations rethinking product, design
            and AI at the operating model level.
          </span>
        </AnimatedSection>
      </div>

      {/* Contact Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 border-t border-white/10">
        {/* Contact Form */}
        <AnimatedSection
          delay={0.2}
          className="px-6 md:px-8 py-10 md:py-14 border-b md:border-b-0 md:border-r border-white/10 md:col-span-2"
        >
          <span className="text-xs uppercase tracking-widest text-white/40 block mb-6">
            START A CONVERSATION
          </span>
          <ContactForm />
        </AnimatedSection>

        {/* Quick Links + Email */}
        <AnimatedSection delay={0.25} className="px-6 md:px-8 py-10 md:py-14">
          <span className="text-xs uppercase tracking-widest text-white/40 block mb-6">
            QUICK LINKS
          </span>

          <div className="space-y-3 mb-8">
            {[
              { label: "Works", path: "/" },
              { label: "About", path: "/#philosophy" },
              { label: "Thinking", path: "/thinking" },
            ].map((link) => (
              <motion.div
                key={link.label}
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <Link
                  to={link.path}
                  className="font-display text-xl uppercase tracking-tight hover:text-accent transition-colors duration-medium flex items-center gap-2 group min-h-[44px]"
                >
                  {link.label}
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-medium" />
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="border-t border-white/10 pt-6">
            <motion.a
              href="mailto:arjansethi@gmail.com"
              className="font-mono text-xs text-white/40 hover:text-accent transition-colors duration-medium flex items-center gap-2 group"
              whileHover={{ x: 3 }}
              transition={{ duration: 0.2 }}
            >
              <Mail className="w-3.5 h-3.5" />
              arjansethi@gmail.com
            </motion.a>
            <span className="text-xs text-white/30 block mt-2 font-mono">Delhi NCR, IN</span>
          </div>
        </AnimatedSection>
      </div>

      {/* Social Icons Row */}
      <div className="border-t border-white/10 px-6 md:px-8 py-6">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-white/40">
            FOLLOW
          </span>
          <div className="flex items-center gap-3">
            {[
              {
                href: "https://linkedin.com/in/arjansethi",
                icon: (
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                ),
              },
              {
                href: "https://x.com/arjansethi",
                icon: (
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                ),
              },
            ].map((social, index) => (
              <MagneticButton key={index} strength={0.3}>
                <motion.a
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-accent hover:border-accent transition-all duration-medium min-w-[44px] min-h-[44px]"
                  whileHover={{ scale: 1.08, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {social.icon}
                  </svg>
                </motion.a>
              </MagneticButton>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-white/10">
        <motion.div
          className="px-6 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {/* Logo */}
          <MagneticButton strength={0.3}>
            <motion.div
              className="w-10 h-10 bg-white text-[hsl(var(--block-dark))] flex items-center justify-center rounded-lg min-w-[44px] min-h-[44px]"
              whileHover={{ scale: 1.08, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <span className="font-display text-xl">A.</span>
            </motion.div>
          </MagneticButton>

          {/* Copyright */}
          <div className="flex items-center gap-8 text-xs text-white/40">
            <span>Arjan © {currentYear}</span>
            <span>All rights reserved</span>
          </div>

          {/* Back to top */}
          <motion.button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="text-xs text-white/60 hover:text-white transition-colors duration-medium flex items-center gap-2 group bg-white/5 px-4 py-2 rounded-lg border border-white/10 hover:border-white/20 min-h-[44px]"
            whileHover={{ y: -2 }}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 10l7-7m0 0l7 7m-7-7v18"
              />
            </svg>
            Back to top
          </motion.button>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
